import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../services/product-characteristics-translation.services';
import {
	CommonColDef, DimensionColDef, DateColDef
} from '../../column-defs';
import { CheckboxHeaderComponent } from '../../header-components/checkbox-header.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { ProductA2c } from '../../../../eorder/models/eorder';
import { ColumnType } from '../../column-type';

export class ProductListColumnDefinition {
	readonly emptyRow: string;
	readonly PRODUCT_LIST_REQUIRED_FIELDS = ['customerMaterialNumber'];
	// eslint-disable-next-line @typescript-eslint/naming-convention
	PRODUCT_LIST_DEFAULT_CONFIGURATION: () => Map<string, CommonColDef<ProductA2c> | CommonColDef<ProductA2c, string>>;

	constructor(
		private readonly translationService: TranslationService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly nameSpace?: string
	) {
		if (this.nameSpace === undefined) {
			this.nameSpace = NAMESPACE_RTPC_FIELD;
		}
		this.emptyRow = this.translationService.getTR('eorder.product.empty.row');
		this.PRODUCT_LIST_DEFAULT_CONFIGURATION = () =>
			new Map<string, CommonColDef<ProductA2c> | CommonColDef<ProductA2c, string>>([
				[
					'customerMaterialNumber',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }usePartReference`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.userPartReference : '',
						suppressHeaderMenuButton: false,
						colId: 'customerMaterialNumber',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'steelGrade',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }steelGrade`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.commercialSteelGradeIDDescription : '',
						suppressHeaderMenuButton: false,
						colId: 'steelGrade',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'metallicCoating',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }metallicCoating`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.commercialMetallicCoatingIDDescription : '',
						suppressHeaderMenuButton: false,
						colId: 'metallicCoating',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'dimension',
					new DimensionColDef<ProductA2c>({
						headerName: `${ this.translationService.getTR('eorder.product.dimension') } (mm)`,
						dimensionGetter(params) {
							return {
								width: params.data?.widthValue ?? 0,
								thickness: params.data?.thicknessValue ?? 0,
								length: params.data?.lengthValue ?? 0
							};
						},
						fields: ['thicknessValue', 'widthValue', 'lengthValue'],
						colId: 'dimension',
						sortable: true,
						filter: DatacenterDimensionFilterComponent,
						headerComponent: CheckboxHeaderComponent,
						suppressHeaderMenuButton: false,
						width: 150,
						columnType: ColumnType.DIMENSION_COLUMN
					})
				],
				[
					'thicknessValue',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thicknessValue`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? this.convertValToNum(params.data.thicknessValue) : '',
						suppressHeaderMenuButton: false,
						colId: 'thicknessValue',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'widthValue',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }widthValue`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? this.convertValToNum(params.data.widthValue) : '',
						suppressHeaderMenuButton: false,
						colId: 'widthValue',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'lengthValue',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }lengthValue`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? this.convertValToNum(params.data.lengthValue) : '',
						suppressHeaderMenuButton: false,
						colId: 'lengthValue',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'surfaceAspect',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }surfaceAspect`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? params.data.surfaceAspect : '',
						suppressHeaderMenuButton: false,
						colId: 'surfaceAspect',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'organicDatasheetNameTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }organicDatasheetNameTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.organicDatasheetNameTop : '',
						suppressHeaderMenuButton: false,
						colId: 'organicDatasheetNameTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'colourTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }colourTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.colourTop : '',
						suppressHeaderMenuButton: false,
						colId: 'colourTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'strippableFilmType',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }strippableFilmType`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.strippableFilmType : '',
						suppressHeaderMenuButton: false,
						colId: 'strippableFilmType',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'organicDatasheetNameBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }organicDatasheetNameBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.organicDatasheetNameBottom : '',
						suppressHeaderMenuButton: false,
						colId: 'organicDatasheetNameBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'colourBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }colourBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.colourBottom : '',
						suppressHeaderMenuButton: false,
						colId: 'colourBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'chemicalTreatment',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }chemicalTreatment`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? params.data.chemicalTreatment : '',
						suppressHeaderMenuButton: false,
						colId: 'chemicalTreatment',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'edgeTrimmedInLastTool',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }edgeTrimmedInLastTool`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.edgeTrimmedInLastToolCodeDescription : '',
						suppressHeaderMenuButton: false,
						colId: 'edgeTrimmedInLastTool',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'floorPlatesPattern',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }floorPlatesPattern`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? `${ params.data.floorPlatesPattern ?? '' } ${ params.data.floorPlatesPatternSide ?? '' }`
							: '',
						suppressHeaderMenuButton: false,
						colId: 'floorPlatesPattern',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'roughness',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }roughness`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.streakinessMaxInMicron : '',
						suppressHeaderMenuButton: false,
						colId: 'roughness',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'thicknessTolerance',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thicknessTolerance`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? this.convertValToNum(params.data.commercialThicknessToleranceIDDescription) : '',
						suppressHeaderMenuButton: false,
						colId: 'thicknessTolerance',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'thicknessToleranceMinus',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thicknessToleranceMinus`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? this.convertValToNum(params.data.thicknessToleranceMinus) : '',
						suppressHeaderMenuButton: false,
						colId: 'thicknessToleranceMinus',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'thicknessTolerancePlus',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thicknessTolerancePlus`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data
							? this.convertValToNum(params.data.thicknessTolerancePlus) : '',
						suppressHeaderMenuButton: false,
						colId: 'thicknessTolerancePlus',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'varnishTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }varnishTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.varnishTop : '',
						suppressHeaderMenuButton: false,
						colId: 'varnishTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'varnishBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }varnishBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.varnishBottom : '',
						suppressHeaderMenuButton: false,
						colId: 'varnishBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'primerTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }primerTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.primerTop : '',
						suppressHeaderMenuButton: false,
						colId: 'primerTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'primerBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }primerBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.primerBottom : '',
						suppressHeaderMenuButton: false,
						colId: 'primerBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'thinOCTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thinOCTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.commercialTOCTypeTopIDDescription : '',
						suppressHeaderMenuButton: false,
						colId: 'thinOCTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'thinOCBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }thinOCBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.commercialTOCTypeBottomIDDescription : '',
						suppressHeaderMenuButton: false,
						colId: 'thinOCBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'nominalThicknessValueBottom',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }nominalThicknessValueBottom`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.nominalThicknessValueBottom : '',
						suppressHeaderMenuButton: false,
						colId: 'nominalThicknessValueBottom',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'nominalThicknessValueTop',
					new CommonColDef<ProductA2c>({
						headerName: this.productCharacteristicsTranslationService
							.translate(`${ this.nameSpace }nominalThicknessValueTop`),
						filter: DatacenterTextFilterComponent,
						sortable: true,
						valueGetter: params => params.data ? params.data.nominalThicknessValueTop : '',
						suppressHeaderMenuButton: false,
						colId: 'nominalThicknessValueTop',
						columnType: ColumnType.TEXT_COLUMN
					})
				],
				[
					'lastUsed',
					new DateColDef<ProductA2c>({
						headerName: this.translationService.getTR('eorder.product.lastused'),
						colId: 'lastUsed',
						field: 'lastUsed',
						minWidth: 120,
						maxWidth: 120,
						width: 120,
						cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
						filter: DatacenterDateFilterComponent,
						headerComponent: CheckboxHeaderComponent,
						valueGetter: params => params.data?.lastUsed || '',
						suppressHeaderMenuButton: false,
						resizable: true,
						columnType: ColumnType.DATE_COLUMN
					}),
				]
			]);
	}

	private convertValToNum(val: string | undefined) {
		const parsedNumber = Number(val);
		if (Number.isNaN(parsedNumber)) {
			return val;
		}
		return parsedNumber;
	}
}

export const DEFAULT_VIEW_PRODUCT_LIST: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerMaterialNumber',
		},
		{
			colId: 'steelGrade',
		},
		{
			colId: 'metallicCoating',
		},
		{
			colId: 'dimension',
		},
		{
			colId: 'surfaceAspect',
		},
		{
			colId: 'lastUsed',
		}
	],
	sortModel: [
		{
			colId: 'lastUsed',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'product-list',
	isDefault: true,
	name: 'Default',
	id: -99
};

export const ORGANIC_COATED_VIEW_PRODUCT_LIST: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerMaterialNumber',
		},
		{
			colId: 'steelGrade',
		},
		{
			colId: 'dimension',
		},
		{
			colId: 'organicDatasheetNameTop',
		},
		{
			colId: 'colourTop',
		},
		{
			colId: 'strippableFilmType',
		},
		{
			colId: 'organicDatasheetNameBottom',
		},
		{
			colId: 'colourBottom',
		},
		{
			colId: 'lastUsed',
		}
	],
	sortModel: [
		{
			colId: 'lastUsed',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'product-list',
	isDefault: true,
	name: 'Default',
	id: -99
};
